import React, { useEffect, useState } from 'react';
import { 
    getAllDevices, 
    registerDevice, 
    updateDeviceAuth, 
    deleteDevice,
    getFieldsByVenue,
    updateDeviceField
} from '../../services/api';
import { 
    setupDeviceId, 
    getDeviceIdFromCookie, 
    setDeviceIdCookie, 
    setDeviceAuthCookie,
    logDeviceCookies,
    updateDeviceAuthCookie
} from '../../utils/deviceFingerprint';
import '../../styles/DeviceManagement.css';

interface Device {
    id: number;
    deviceId: string;
    venueId: number | null;
    venueName: string;
    fieldId: number | null;
    fieldName: string;
    isAuthorized: boolean;
    createdAt: string;
}

interface Venue {
    id: string;
    name: string;
}

interface Field {
    id: number;
    name: string;
    venueId: number;
}

interface DeviceManagementProps {
    venues: Venue[];
}

const DeviceManagement: React.FC<DeviceManagementProps> = ({ venues }) => {
    const [devices, setDevices] = useState<Device[]>([]);
    const [currentDeviceId, setCurrentDeviceId] = useState<string>('');
    const [selectedVenue, setSelectedVenue] = useState<string>('');
    const [filterVenue, setFilterVenue] = useState<string>('all');
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [deleteConfirmation, setDeleteConfirmation] = useState<string | null>(null);
    const [isCurrentDeviceRegistered, setIsCurrentDeviceRegistered] = useState<boolean>(false);
    const [cookieDeviceId, setCookieDeviceId] = useState<string | null>(null);
    const [hasMismatch, setHasMismatch] = useState<boolean>(false);
    const [fieldsForRegistration, setFieldsForRegistration] = useState<Field[]>([]);
    const [loadingRegistrationFields, setLoadingRegistrationFields] = useState<boolean>(false);
    const [selectedRegistrationField, setSelectedRegistrationField] = useState<string>('');

    useEffect(() => {
        const initializeDeviceId = async () => {
            try {
                // Get device ID from cookie
                const storedDeviceId = getDeviceIdFromCookie();
                setCookieDeviceId(storedDeviceId);
                
                // Use setupDeviceId to get consistent device ID from cookie or generate if needed
                const deviceId = await setupDeviceId();
                setCurrentDeviceId(deviceId);
                console.log('Current device ID:', deviceId);
                
                // Check for mismatch between stored ID and fingerprint
                if (storedDeviceId && deviceId !== storedDeviceId) {
                    console.warn('Device ID mismatch detected:', { stored: storedDeviceId, current: deviceId });
                    setHasMismatch(true);
                }
            } catch (err) {
                console.error('Failed to get device ID:', err);
                setError('Failed to get device ID. Please try again.');
            }
        };

        initializeDeviceId();
        fetchDevices();
    }, []);

    // When devices are loaded, check if current device exists and update cookie if needed
    useEffect(() => {
        if (currentDeviceId && devices.length > 0) {
            // Find the current device in the devices list
            const currentDevice = devices.find(device => device.deviceId === currentDeviceId);
            
            if (currentDevice) {
                // If device is found, update the cookie with current authorization status
                console.log('Syncing device auth cookie with server status:', currentDevice.isAuthorized);
                // First log the current cookies
                logDeviceCookies();
                // Then update the auth cookie to match server status
                const updated = updateDeviceAuthCookie(currentDeviceId, devices);
                if (updated) {
                    console.log('Auth cookie updated to match server');
                } else {
                    console.log('Auth cookie already matched server');
                }
            }
        }
    }, [devices, currentDeviceId]);

    // Check if current device is already registered when devices or current ID changes
    useEffect(() => {
        if (currentDeviceId && devices.length > 0) {
            const deviceExists = devices.some(device => device.deviceId === currentDeviceId);
            setIsCurrentDeviceRegistered(deviceExists);
        }
    }, [devices, currentDeviceId]);

    // Update to fetch fields when venue is selected for registration
    useEffect(() => {
        if (selectedVenue) {
            fetchFieldsForVenue(selectedVenue);
        } else {
            setFieldsForRegistration([]);
            setSelectedRegistrationField('');
        }
    }, [selectedVenue]);

    const fetchDevices = async () => {
        try {
            setLoading(true);
            const response = await getAllDevices();
            setDevices(response);
            setError(null);
        } catch (err: any) {
            console.error('Failed to fetch devices:', err);
            setError(err.response?.data?.message || 'Failed to load devices');
        } finally {
            setLoading(false);
        }
    };

    // Fetch fields for a venue during registration
    const fetchFieldsForVenue = async (venueId: string) => {
        try {
            setLoadingRegistrationFields(true);
            const response = await getFieldsByVenue(venueId);
            setFieldsForRegistration(response);
        } catch (err: any) {
            console.error('Failed to fetch fields for venue:', err);
            setError(err.response?.data?.message || 'Failed to load fields');
        } finally {
            setLoadingRegistrationFields(false);
        }
    };

    const handleRegister = async () => {
        if (!selectedVenue) {
            setError('Venue selection is required');
            return;
        }
        
        try {
            setLoading(true);
            // Register the device with the API
            const result = await registerDevice(currentDeviceId, selectedVenue);
            console.log('Device registration result:', result);
            
            // Set cookies to match registered device
            setDeviceIdCookie(currentDeviceId);
            
            // Check if the API response includes authorization status
            const isAuthorized = result?.isAuthorized === true;
            console.log('Setting device auth cookie to:', isAuthorized);
            setDeviceAuthCookie(isAuthorized); 
            
            // Immediately update the UI to show this device is registered
            setIsCurrentDeviceRegistered(true);
            
            // Get the registered device object
            let registeredDevice = result.device || {
                id: Date.now(), // Temporary ID until we refresh from server
                deviceId: currentDeviceId,
                venueId: parseInt(selectedVenue),
                venueName: venues.find(v => v.id === selectedVenue)?.name || 'Unknown',
                fieldId: null,
                fieldName: 'Not assigned',
                isAuthorized,
                createdAt: new Date().toISOString()
            };
            
            // If a field was selected, update the device with the field
            if (selectedRegistrationField) {
                try {
                    const fieldId = parseInt(selectedRegistrationField);
                    console.log(`Updating device ${currentDeviceId} with field ${fieldId}`);
                    
                    const updatedDevice = await updateDeviceField(currentDeviceId, fieldId);
                    console.log('Field update result:', updatedDevice);
                    
                    // Use the updated device if available
                    if (updatedDevice) {
                        registeredDevice = updatedDevice;
                    } else {
                        // Just update our local copy with the field info
                        const fieldName = fieldsForRegistration.find(f => f.id === fieldId)?.name || 'Unknown';
                        registeredDevice.fieldId = fieldId;
                        registeredDevice.fieldName = fieldName;
                    }
                } catch (fieldErr) {
                    console.error('Error updating field (continuing):', fieldErr);
                    // Don't fail the whole registration process if field update fails
                }
            }
            
            // Update the devices list with our new or updated device
            setDevices(prevDevices => [
                registeredDevice,
                ...prevDevices.filter(d => d.deviceId !== currentDeviceId)
            ]);
            
            // Also fetch from the server to ensure data consistency
            fetchDevices();
            setSelectedVenue('');
            setSelectedRegistrationField('');
            setError(null);
        } catch (err: any) {
            console.error('Failed to register device:', err);
            setError(err.response?.data?.message || 'Failed to register device');
        } finally {
            setLoading(false);
        }
    };

    const confirmDelete = (deviceId: string) => {
        setDeleteConfirmation(deviceId);
    };

    const cancelDelete = () => {
        setDeleteConfirmation(null);
    };

    const handleDelete = async (deviceId: string) => {
        try {
            setLoading(true);
            await deleteDevice(deviceId);
            
            if (deviceId === currentDeviceId) {
                clearDeviceCookies();
                // Update UI state immediately to show registration form
                setIsCurrentDeviceRegistered(false);
            }
            
            // Update devices list immediately by removing the deleted device
            setDevices(prevDevices => prevDevices.filter(d => d.deviceId !== deviceId));
            
            // Still fetch the latest data from server
            fetchDevices();
            setDeleteConfirmation(null);
            setError(null);
        } catch (err: any) {
            console.error('Failed to delete device:', err);
            setError(err.response?.data?.message || 'Failed to delete device');
        } finally {
            setLoading(false);
        }
    };

    const toggleAuthorization = async (deviceId: string, isAuthorized: boolean) => {
        try {
            setLoading(true);
            // The API call toggles the current state to its opposite
            const newAuthStatus = !isAuthorized;
            console.log(`Toggling device ${deviceId} authorization to: ${newAuthStatus}`);
            
            await updateDeviceAuth(deviceId, newAuthStatus);
            
            // If this is the current device, update authorization cookie with the new status
            if (deviceId === currentDeviceId) {
                console.log('Updating device auth cookie to:', newAuthStatus);
                // Log before
                logDeviceCookies();
                
                // Set the new status
                setDeviceAuthCookie(newAuthStatus);
                
                // Log after to verify
                setTimeout(() => {
                    console.log('After update:');
                    logDeviceCookies();
                }, 100);
            }
            
            fetchDevices();
            setError(null);
        } catch (err: any) {
            console.error('Failed to update device authorization:', err);
            setError(err.response?.data?.message || 'Failed to update device authorization');
        } finally {
            setLoading(false);
        }
    };

    const clearDeviceCookies = () => {
        document.cookie = 'clipo_device_id=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
        document.cookie = 'clipo_device_auth=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
        console.log('Device cookies cleared');
        
        // Update the cookie device ID state to reflect the cleared cookie
        setCookieDeviceId(null);
    };

    // Function to sync device IDs when there's a mismatch
    const syncDeviceId = async () => {
        try {
            const newId = await setupDeviceId(); // This will generate a new ID and set the cookie
            setCurrentDeviceId(newId);
            setCookieDeviceId(newId);
            setHasMismatch(false);
            setError(null);
            // Reload the component to refresh everything
            fetchDevices();
        } catch (err) {
            console.error('Failed to sync device ID:', err);
            setError('Failed to sync device ID. Please try again.');
        }
    };

    // Filter devices based on selected venue
    const filteredDevices = filterVenue === 'all' 
        ? devices 
        : devices.filter(device => device.venueId?.toString() === filterVenue);

    return (
        <div className="device-management">
            <h2>Device Management</h2>
            
            {error && <div className="error-message">{error}</div>}
            
            {hasMismatch && (
                <div className="mismatch-warning">
                    <p>Device ID mismatch detected between cookie and fingerprint!</p>
                    <p>Cookie: {cookieDeviceId}</p>
                    <p>Fingerprint: {currentDeviceId}</p>
                    <button onClick={syncDeviceId} className="sync-button">
                        Reset and Sync Device ID
                    </button>
                </div>
            )}
            
            <div className="device-form">
                <div className="form-group">
                    <label>Current Device ID</label>
                    <input
                        type="text"
                        value={currentDeviceId}
                        readOnly
                        className="device-id-display"
                    />
                    {isCurrentDeviceRegistered && (
                        <div className="device-status-message registered">
                            This device is registered
                            {devices.find(d => d.deviceId === currentDeviceId)?.isAuthorized 
                                ? " and authorized" 
                                : " but not authorized"}
                        </div>
                    )}
                </div>
                
                {!isCurrentDeviceRegistered && (
                    <>
                        <div className="form-group">
                            <label>Assign to Venue <span className="required">*</span></label>
                            <select 
                                value={selectedVenue}
                                onChange={(e) => setSelectedVenue(e.target.value)}
                                required
                            >
                                <option value="">Select a venue</option>
                                {venues.map(venue => (
                                    <option key={venue.id} value={venue.id}>
                                        {venue.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {selectedVenue && (
                            <div className="form-group">
                                <label>Assign to Court</label>
                                <select 
                                    value={selectedRegistrationField}
                                    onChange={(e) => setSelectedRegistrationField(e.target.value)}
                                    disabled={loadingRegistrationFields}
                                >
                                    <option value="">Not Assigned</option>
                                    {fieldsForRegistration.map(field => (
                                        <option key={field.id} value={field.id}>
                                            {field.name}
                                        </option>
                                    ))}
                                </select>
                                {loadingRegistrationFields && <div className="loading-fields">Loading fields...</div>}
                            </div>
                        )}
                    
                        <button 
                            onClick={handleRegister}
                            disabled={loading || !currentDeviceId || !selectedVenue}
                            className="register-device-button"
                        >
                            Register Device
                        </button>
                        
                        {!selectedVenue && (
                            <div className="button-description">
                                Please select a venue to register this device
                            </div>
                        )}
                    </>
                )}
            </div>
            
            <div className="device-filter">
                <h3>Registered Devices</h3>
                <div className="filter-controls">
                    <label htmlFor="venue-filter">Filter by Venue:</label>
                    <select 
                        id="venue-filter"
                        value={filterVenue}
                        onChange={(e) => setFilterVenue(e.target.value)}
                    >
                        <option value="all">All Venues</option>
                        {venues.map(venue => (
                            <option key={venue.id} value={venue.id}>
                                {venue.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            
            {loading && filteredDevices.length === 0 ? (
                <div className="loading">Loading devices...</div>
            ) : filteredDevices.length === 0 ? (
                <div className="no-devices">No devices found</div>
            ) : (
                <div className="devices-table-container">
                    <table className="devices-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Device ID</th>
                                <th>Venue</th>
                                <th>Field</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDevices.map(device => (
                                <tr key={device.id} className={device.deviceId === currentDeviceId ? 'current-device' : ''}>
                                    <td>{device.id}</td>
                                    <td className="device-id-cell">
                                        {device.deviceId}
                                        {device.deviceId === currentDeviceId && (
                                            <span className="current-device-badge">Current Device</span>
                                        )}
                                    </td>
                                    <td>{device.venueName}</td>
                                    <td>{device.fieldName}</td>
                                    <td>
                                        <span className={`status-badge ${device.isAuthorized ? 'authorized' : 'unauthorized'}`}>
                                            {device.isAuthorized ? 'Authorized' : 'Unauthorized'}
                                        </span>
                                    </td>
                                    <td>{new Date(device.createdAt).toLocaleDateString()}</td>
                                    <td className="actions-cell">
                                        <button
                                            className={`auth-toggle-button ${device.isAuthorized ? 'deauthorize' : 'authorize'}`}
                                            onClick={() => toggleAuthorization(device.deviceId, device.isAuthorized)}
                                        >
                                            {device.isAuthorized ? 'Deauthorize' : 'Authorize'}
                                        </button>
                                        
                                        {deleteConfirmation === device.deviceId ? (
                                            <div className="delete-confirmation">
                                                <span>Are you sure?</span>
                                                <button 
                                                    className="confirm-delete"
                                                    onClick={() => handleDelete(device.deviceId)}
                                                >
                                                    Yes
                                                </button>
                                                <button 
                                                    className="cancel-delete"
                                                    onClick={cancelDelete}
                                                >
                                                    No
                                                </button>
                                            </div>
                                        ) : (
                                            <button 
                                                className="delete-device-button"
                                                onClick={() => confirmDelete(device.deviceId)}
                                            >
                                                Delete
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default DeviceManagement;